import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Search, RefreshCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { esCpIntl, esEmail, esIdFiscal } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";
import { useAuth } from "@/auth/AuthContext";

const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const A = String.fromCharCode(0xe1);
const U = String.fromCharCode(0xfa);
const N = String.fromCharCode(0xf1);
const DASH = "\u2014";

const EMPTY = {
  razon_social: "",
  nif: "",
  domicilio: "",
  municipio: "",
  cp: "",
  provincia: "",
  email: "",
};

const PAGE_SIZE = 50;

function esSage(t) {
  return t?.sage === true || t?.fuente === "sage" || String(t?.id || "").startsWith("sage-");
}

function esLocalId(id) {
  const s = String(id || "");
  return s && !s.startsWith("sage-");
}

export default function ProveedoresMantPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sage, setSage] = useState(user?.sage_conectado === true);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/v1/partes", {
        params: { tipo: "proveedor", page, page_size: PAGE_SIZE, buscar: q || undefined },
      });
      setItems(data.items || []);
      setTotal(data.total == null ? (data.items || []).length : Number(data.total));
      setSage(data.sage === true || user?.sage_conectado === true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const next = qInput.trim();
      setQ((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => { if (user?.deca_proveedores) load(); }, [page, q]); // eslint-disable-line

  if (!user?.deca_proveedores) return <Navigate to="/" replace />;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const hasMore = safePage < pageCount;

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEdit(t);
    setForm({
      razon_social: t.razon_social || "",
      nif: t.nif || "",
      domicilio: t.domicilio || "",
      municipio: t.municipio || "",
      cp: t.cp || "",
      provincia: t.provincia || "",
      email: t.email || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.razon_social.trim()) {
      toast.error("Indica la raz" + O + "n social");
      return;
    }
    if (!form.nif.trim()) {
      toast.error("Indica el NIF");
      return;
    }
    if (!esIdFiscal(form.nif)) {
      toast.error("Identificador fiscal no v" + A + "lido. NIF/CIF/NIE espa" + N + "ol (letra de control) o VAT extranjero (p. ej. PT123456789).");
      return;
    }
    if (form.cp.trim() && !esCp(form.cp)) {
      toast.error("C" + O + "digo postal no v" + A + "lido (5 d" + I + "gitos)");
      return;
    }
    if (form.email.trim() && !esEmail(form.email)) {
      toast.error("Correo no v" + A + "lido");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        razon_social: form.razon_social.trim(),
        nif: form.nif.trim(),
        domicilio: form.domicilio.trim(),
        municipio: form.municipio.trim(),
        cp: form.cp.trim(),
        provincia: form.provincia.trim(),
        email: form.email.trim() || null,
      };
      if (edit && esLocalId(edit.id)) await api.patch(`/api/v1/partes/${edit.id}`, payload);
      else await api.post("/api/v1/partes", { tipo: "proveedor", ...payload });
      setOpen(false);
      await load();
      toast.success(edit ? "Proveedor actualizado" : "Proveedor creado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (esSage(t)) {
      toast.error("Este proveedor viene de Sage y no se puede eliminar aqu" + I);
      return;
    }
    const ok = window.confirm("Eliminar el proveedor " + (t.razon_social || t.nif) + "?");
    if (!ok) return;
    try {
      await api.delete(`/api/v1/partes/${t.id}`);
      await load();
      toast.success("Proveedor eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const domicilioTxt = (t) => {
    const dom = t.domicilio || "";
    if (dom && String(t.cp || "") && dom.toLowerCase().includes(String(t.cp).toLowerCase())) return dom;
    return [t.domicilio, t.cp, t.municipio, t.provincia].filter(Boolean).join(", ") || DASH;
  };

  return (
    <div>
      <PageHeader
        title="Proveedores"
        subtitle={sage
          ? "Proveedores de Sage. El domicilio se usa como origen del DeCA a proveedor."
          : "Cat" + A + "logo de proveedores de tu empresa. El origen del DeCA a proveedor sale de aqu" + I + "."}
        testid="page-proveedores-mant"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="btn-proveedores-refresh">
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Actualizar
            </Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="btn-proveedor-nuevo">
              <Plus className="h-4 w-4 mr-2"/> Nuevo proveedor
            </Button>
          </div>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[16rem]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
            <Input
              className="pl-8"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder={"Buscar raz" + O + "n social, NIF o poblaci" + O + "n"}
              aria-label="Buscar proveedores"
              data-testid="proveedores-buscar"
            />
          </div>
          <div className="text-xs text-slate-500" data-testid="proveedores-counter">
            {loading ? "Cargando" + String.fromCharCode(0x2026) : (
              <>
                <span className="font-semibold text-slate-800">{items.length}</span>
                {total > items.length ? ` de ${total.toLocaleString("es-ES")}` : null}
                {" proveedores"}
                {sage ? " · Sage" : ""}
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="deca-table w-full min-w-[720px]">
            <thead>
              <tr>
                <th>{"Raz" + O + "n social"}</th>
                <th>NIF</th>
                <th>Domicilio</th>
                <th>Correo</th>
                <th>Fuente</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`proveedor-row-${t.id}`}>
                  <td className="font-medium">
                    {t.razon_social || DASH}
                    {t.codigo != null && t.codigo !== "" ? (
                      <div className="text-[11px] font-mono text-slate-400">C{O}d. {t.codigo}</div>
                    ) : null}
                  </td>
                  <td className="font-mono text-xs">{t.nif || DASH}</td>
                  <td className="text-sm text-slate-600 max-w-[280px] truncate">{domicilioTxt(t)}</td>
                  <td className="text-sm">{t.email || DASH}</td>
                  <td>
                    {esSage(t) ? (
                      <span className="status-badge status-generado">Sage</span>
                    ) : (
                      <span className="text-xs text-slate-400">Manual</span>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-2" onClick={() => openEdit(t)} aria-label="Editar proveedor" data-testid={`btn-edit-prv-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true"/>
                    </Button>
                    {esSage(t) ? null : (
                      <Button size="sm" variant="outline" className="text-red-700" onClick={() => remove(t)} aria-label="Eliminar proveedor" data-testid={`btn-del-prv-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true"/>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    <Package className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
                    {loading
                      ? "Cargando proveedores" + String.fromCharCode(0x2026)
                      : total === 0
                        ? (sage ? "Sage no ha devuelto proveedores para esta empresa." : "No hay proveedores. Crea el primero.")
                        : "No hay proveedores con esa b" + U + "squeda."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-t border-slate-200 bg-slate-50" data-testid="proveedores-pagination">
          <div className="text-xs text-slate-500">
            P{A}gina <span className="font-semibold text-slate-900">{safePage}</span>
            {total > 0 ? <> · {pageCount.toLocaleString("es-ES")} p{A}ginas · {total.toLocaleString("es-ES")} registros</> : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1 || loading} data-testid="proveedores-prev">
              <ChevronLeft className="h-4 w-4 mr-1"/> Anterior
            </Button>
            <span className="text-xs font-mono text-slate-700 px-2">P{A}g. {safePage}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={!hasMore || loading} data-testid="proveedores-next">
              Siguiente <ChevronRight className="h-4 w-4 ml-1"/>
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field className="sm:col-span-2" label={"Raz" + O + "n social *"}>
              <Input className="mt-1" maxLength={LEN.razonSocial} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="prv-razon"/>
            </Field>
            <Field label="NIF / VAT *">
              <Input className="mt-1 font-mono uppercase" maxLength={LEN.nif} value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} data-testid="prv-nif"/>
            </Field>
            <Field label={"C" + O + "digo postal"}>
              <Input className="mt-1" inputMode="numeric" maxLength={LEN.cp} value={form.cp} onChange={(e) => setForm({ ...form, cp: e.target.value })} data-testid="prv-cp"/>
            </Field>
            <Field className="sm:col-span-2" label="Domicilio">
              <Input className="mt-1" maxLength={LEN.domicilio} value={form.domicilio} onChange={(e) => setForm({ ...form, domicilio: e.target.value })} data-testid="prv-domicilio"/>
            </Field>
            <Field label="Municipio">
              <Input className="mt-1" maxLength={LEN.municipio} value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="prv-municipio"/>
            </Field>
            <Field label="Provincia">
              <Input className="mt-1" maxLength={LEN.provincia} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} data-testid="prv-provincia"/>
            </Field>
            <Field className="sm:col-span-2" label={"Correo de env" + I + "o"}>
              <Input className="mt-1" type="email" maxLength={LEN.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="pedidos@proveedor.com" data-testid="prv-email"/>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy} data-testid="prv-guardar">
              {busy ? "Guardando" + String.fromCharCode(0x2026) : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
